import { useState, useEffect } from 'react';
import {
  Folder, Plus, FolderOpen, Trash2, RefreshCw, Download, Upload,
  TerminalSquare, Clipboard, PlaySquare, Loader2, ExternalLink,
} from 'lucide-react';
import { useStore } from '../../store/useStore';
import { api } from '../../services/api';
import type { Project } from '../../types';

export function ProjectsApp() {
  const projects = useStore((s) => s.projects);
  const loadingProjects = useStore((s) => s.loadingProjects);
  const loadProjects = useStore((s) => s.loadProjects);
  const createProject = useStore((s) => s.createProject);
  const openProject = useStore((s) => s.openProject);
  const deleteProject = useStore((s) => s.deleteProject);
  const syncProject = useStore((s) => s.syncProject);
  const currentProject = useStore((s) => s.currentProject);
  const openApp = useStore((s) => s.openApp);
  const addNotification = useStore((s) => s.addNotification);
  const hasGoogleAccess = useStore((s) => s.hasGoogleAccess);

  const [newProjectName, setNewProjectName] = useState('');
  const [creating, setCreating] = useState(false);
  const [syncingId, setSyncingId] = useState<string | null>(null);

  useEffect(() => {
    loadProjects();
  }, []);

  const handleCreate = async () => {
    if (!newProjectName.trim() || creating) return;
    setCreating(true);
    try {
      const project = await createProject(newProjectName.trim());
      setNewProjectName('');
    } catch (err: any) {
      addNotification('Error', err.message || 'Failed to create project', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleSync = async (project: Project, direction: 'up' | 'down') => {
    setSyncingId(project.id);
    try {
      await syncProject(project.id, direction);
    } finally {
      setSyncingId(null);
    }
  };

  const handleOpen = (project: Project) => {
    openProject(project);
    addNotification('Info', `Opened "${project.name}"`, 'info');
  };

  // Verify Google access exists
  if (!hasGoogleAccess) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-4 p-8 bg-desktop-surface2">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
          <Folder className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-lg font-semibold text-desktop-text">Connect Google Drive</h2>
        <p className="text-sm text-desktop-text/60 text-center max-w-sm">
          Projects are stored in Google Drive. Connect your Google account to get started.
        </p>
        <button
          onClick={async () => {
            try {
              const { url } = await api.getGoogleAuthUrl();
              window.location.href = url;
            } catch {}
          }}
          className="px-4 py-2 bg-desktop-accent text-white rounded-lg hover:opacity-90 text-sm font-medium"
        >
          Connect Google Drive
        </button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-desktop-surface2">
      {/* Header */}
      <div className="px-4 py-3 border-b border-desktop-border bg-desktop-surface shrink-0">
        <h2 className="text-sm font-medium text-desktop-text mb-2">My Projects</h2>
        <div className="flex items-center gap-2">
          <input
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
            placeholder="New project name..."
            className="flex-1 px-3 py-1.5 text-sm bg-desktop-surface2 border border-desktop-border rounded-lg text-desktop-text placeholder:text-desktop-text/40 focus:outline-none focus:border-desktop-accent"
          />
          <button
            onClick={handleCreate}
            disabled={creating || !newProjectName.trim()}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-desktop-accent text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Create
          </button>
        </div>
      </div>

      {/* Project list */}
      <div className="flex-1 overflow-y-auto p-3">
        {loadingProjects ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 text-desktop-accent animate-spin" />
          </div>
        ) : projects.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-desktop-text/40 gap-2">
            <Folder className="w-14 h-14" />
            <p className="text-sm">No projects yet</p>
            <p className="text-xs">Create your first project above</p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {projects.map((project) => (
              <div
                key={project.id}
                className={`p-3 rounded-xl border transition-colors ${
                  currentProject?.id === project.id
                    ? 'border-desktop-accent/50 bg-desktop-accent/10'
                    : 'border-desktop-border bg-desktop-surface hover:border-desktop-border/70'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                    currentProject?.id === project.id
                      ? 'bg-desktop-accent/20'
                      : 'bg-desktop-surface2'
                  }`}>
                    <Folder className={`w-5 h-5 ${currentProject?.id === project.id ? 'text-desktop-accent' : 'text-amber-400'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-desktop-text truncate">{project.name}</p>
                    <p className="text-[10px] text-desktop-text/50">
                      Updated {project.updatedAt ? new Date(project.updatedAt).toLocaleString() : 'recently'}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => openApp('terminal', { projectName: project.name, projectId: project.id })}
                      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-desktop-hover text-desktop-text/70 hover:text-desktop-text transition-colors"
                      title="Open terminal in project"
                    >
                      <TerminalSquare className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => openApp('editor', { projectId: project.id, title: project.name })}
                      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-desktop-hover text-desktop-text/70 hover:text-desktop-text transition-colors"
                      title="Open code editor"
                    >
                      <Clipboard className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => { handleOpen(project); openApp('file-manager', { initialFolderId: project.googleDriveId }); }}
                      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-desktop-hover text-desktop-text/70 hover:text-desktop-text transition-colors"
                      title="Browse project files"
                    >
                      <FolderOpen className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {currentProject?.id === project.id && (
                  <div className="mt-2.5 flex items-center gap-1.5 border-t border-desktop-accent/20 pt-2.5">
                    <button
                      onClick={() => handleSync(project, 'down')}
                      disabled={syncingId === project.id}
                      className="flex items-center gap-1 px-2 py-1 text-[11px] rounded-md bg-desktop-surface2 hover:bg-desktop-hover text-desktop-text/70 hover:text-desktop-text transition-colors disabled:opacity-50"
                    >
                      {syncingId === project.id
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <Download className="w-3 h-3" />}
                      Pull from Drive
                    </button>
                    <button
                      onClick={() => handleSync(project, 'up')}
                      disabled={syncingId === project.id}
                      className="flex items-center gap-1 px-2 py-1 text-[11px] rounded-md bg-desktop-surface2 hover:bg-desktop-hover text-desktop-text/70 hover:text-desktop-text transition-colors disabled:opacity-50"
                    >
                      {syncingId === project.id
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <Upload className="w-3 h-3" />}
                      Push to Drive
                    </button>
                    <button
                      onClick={() => openApp('preview', { projectId: project.id, title: `Preview - ${project.name}` })}
                      className="flex items-center gap-1 px-2 py-1 text-[11px] rounded-md bg-desktop-surface2 hover:bg-desktop-hover text-desktop-text/70 hover:text-desktop-text transition-colors"
                    >
                      <PlaySquare className="w-3 h-3" />
                      Preview
                    </button>
                    <div className="flex-1" />
                    <button
                      onClick={() => {
                        if (confirm(`Delete project "${project.name}"? This will also delete files from Google Drive.`)) {
                          deleteProject(project.id);
                        }
                      }}
                      className="flex items-center gap-1 px-2 py-1 text-[11px] rounded-md hover:bg-red-600/20 text-desktop-text/60 hover:text-red-400 transition-colors"
                    >
                      <Trash2 className="w-3 h-3" />
                      Delete
                    </button>
                  </div>
                )}

                {currentProject?.id !== project.id && (
                  <button
                    onClick={() => handleOpen(project)}
                    className="mt-2 w-full text-left px-2 py-1.5 text-xs rounded-md bg-desktop-surface2 hover:bg-desktop-hover text-desktop-text/70 hover:text-desktop-text transition-colors"
                  >
                    Open project
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="px-4 py-2 border-t border-desktop-border bg-desktop-surface shrink-0 flex items-center justify-between">
        <span className="text-[10px] text-desktop-text/50">
          Projects are stored in Google Drive under MyCloudProjects/
        </span>
        <button onClick={loadProjects} className="text-desktop-text/50 hover:text-desktop-text transition-colors" title="Refresh list">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}